// What each Step 2 cell says for one building: its words, its tone and what its hover card
// explains. Shared with the edit pane, which shows the page's own wording beside an override.
import type {
  AmazonBuilding,
  AmazonNeed,
  BuildingStep,
} from "../../api/hooks/useAmazonOverview";
import {
  OPEN_ROUTE_STAGES,
  type OptionRoute,
  parseOptionRoutes,
  routeOrder,
} from "../../utils/optionRoutes";
import { day, daysBetween, formatDate, formatShort, today } from "./dates";
import { blocksItsStep, designInputs, heldItems, owed, stepItems, waitingText } from "./needs";
import { type Overrides, stepKey, wordingOf } from "./overrides";
import { isSettled } from "./progress";
import type { Owner } from "./pathSteps";
import type { Tone as GapTone } from "../../utils/sizeGap";
import { earliestNeededBy, isLateItem, lateInputs } from "./schedule";
import { ppaShortfall, sizeText } from "./sizes";

export type Tone = "done" | "open" | "blocked" | "none" | "na";

export interface Cell {
  tone: Tone;
  text: string;
  sub?: string;
  /** The status in words, for the hover card. */
  status: string;
  /** Anything more the cell needs explaining. */
  note?: string;
  /** A dot beside the cell's figure (``sub``, else ``text``): on the grid offer, how far
   * its size falls short of the PPA's; on the structural assessment, how little of the roof
   * it finds viable. */
  gap?: GapTone;
  /** The name and owner the hover card gives it, where not its step's own. */
  shownAs?: { title: string; owner: Owner };
  /** A qualifier the hover card opens to explain, under ``label``. */
  more?: { label: string; text: string };
  /** Fixed lines the card shows whole, under the status: an email subject may hold a full
   * stop the note's first-sentence cut would split. */
  lines?: string[];
  /** ``sub`` says what its figures count, so it stays even where they match the bold's. */
  subKept?: boolean;
}

/** ``text`` with its first letter in upper case. */
export const capitalised = (text: string): string =>
  text ? `${text[0].toUpperCase()}${text.slice(1)}` : text;

/** The text up to its first sentence's end: a full stop, question or exclamation mark
 * followed by a space and a capital, or the end. "Amazon.com, Inc. within" stays whole. */
export const firstSentence = (text: string): string =>
  text.trim().match(/^[\s\S]*?[.!?](?=\s+[A-Z(]|$)/)?.[0] ?? text.trim();

const MONTH = "jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec";
/** The data a line carries: a date ("3 Aug 2026", "3 Aug"), a figure with its unit ("900
 * kw", "12 days") or a quoted name. */
const DATA = new RegExp(
  `\\d{1,2} (?:${MONTH})[a-z]*(?: \\d{4})?|\\d+(?:\\.\\d+)?(?: ?(?:kwp|kw|mwp|mw|days?|items?|points?|%))?|"[^"]*"`,
  "g",
);

/** Lower case, thousands commas out, kVA and kWac read as kW (Grid writes either for the
 * same capacity), curly quotes as straight ones. */
const normal = (text: string): string =>
  text
    .toLowerCase()
    .replace(/(\d),(\d)/g, "$1$2")
    .replace(/\b(\d+(?:\.\d+)?) ?(?:kva|kwac)\b/g, "$1 kw")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .trim();

const escaped = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** A line saying evidence is missing ("email not on record", "sender not on record", "no
 * email linked"): data in itself, so never dropped as a repeat of a bold value that does
 * not say it. */
const MISSING_EVIDENCE = /\b[a-z]+ not (?:on record|recorded|cited|linked)\b|\bno [a-z]+ (?:linked|cited|on record)\b/g;

/** A line under a bold value repeats nothing new: every date, figure, quoted name and
 * missing-evidence warning in it is in the bold value already, or, holding none, the bold
 * value says it word for word. Every Step 2 cell and hover card drops such a line (Noaa,
 * 17 Oct: "if all of the data is in the bold text just drop the text under it", "for allll
 * of them"). */
export const repeatsBold = (line: string, bold: string): boolean => {
  const said = normal(bold);
  const warnings = normal(line).match(MISSING_EVIDENCE) ?? [];
  if (warnings.some((w) => !said.includes(w.trim()))) return false;
  const data = normal(line).match(DATA) ?? [];
  if (!data.length) return said.includes(normal(line).replace(/[.:]+$/, ""));
  return data.every((d) => new RegExp(`(^|[^\\d])${escaped(d)}($|[^\\d])`).test(said));
};

/** What each column shows, in the hover card, where its status alone does not say. */
export const MEANS: Record<string, string> = {
  structural: "Our structural assessment of the roof.",
  signoff:
    "Amazon's sign-off of our structural assessment, which detailed design waits on. The contract sets no time limit for it.",
  inputs:
    "Amazon's design inputs: drawings, warranties, MFL walls and the rest Step 1 lists.",
  roof: "The independent roof inspection, and any remediation it calls for, before install.",
  dr3: "Amazon's approval of the detailed design (DR3): technical, landlord and insurance reviews.",
  pcg: "One guarantee from Amazon.com, Inc. of Amazon's payments across the portfolio, not one per building. Due within 30 days of the first SPPA's Effective Date; until it is delivered we do not have to start building any site (SPPA Sch 2, s8.5).",
};

/** Steps whose done state carries the size it allows: the grid offer's kWac. */
const SIZED = new Set(["gridoffer"]);

const when = (iso: string): string => {
  const d = day(iso);
  return d.getUTCFullYear() === today().getUTCFullYear()
    ? formatShort(d)
    : formatDate(d);
};

const stepCell = (id: string, step: BuildingStep): Cell => {
  switch (step.state) {
    case "done": {
      const size = id === "gridoffer" ? sizeText(step.kw_ac, "kWac") : "";
      const sized = SIZED.has(id);
      return {
        tone: "done",
        text: "✓",
        sub: size || (sized ? "size not recorded" : undefined),
        status:
          `Done${step.date ? ` on ${formatDate(day(step.date))}` : ""}` +
          (size ? `, allows ${size}` : sized ? ", size not recorded yet" : ""),
      };
    }
    case "open": {
      // A wait with no deadline a document set: how long it has waited, never red.
      if (step.waiting_since) {
        const days = daysBetween(day(step.waiting_since), today());
        const estimate = step.due && step.estimated ? day(step.due) : null;
        return {
          tone: "open",
          text: `${days} days waiting`,
          sub: estimate ? `est. ${when(step.due)}` : undefined,
          status:
            `Waiting ${days} days, since ${formatDate(day(step.waiting_since))}` +
            (estimate ? `; our estimate is ${formatDate(estimate)}` : ""),
        };
      }
      if (!step.due) return { tone: "open", text: "●", status: "In progress" };
      // Past its date with no answer is late, whether the date was given or estimated.
      const due = formatDate(day(step.due));
      const estimate = step.estimated ? " (our estimate)" : "";
      if (day(step.due) < today()) {
        return {
          tone: "blocked",
          text: `was due ${when(step.due)}`,
          status: `Was due ${due}${estimate}, no answer yet`,
        };
      }
      return step.estimated
        ? {
            tone: "open",
            text: `est. ${when(step.due)}`,
            status: `Estimated ${due}`,
          }
        : {
            tone: "open",
            text: `due ${when(step.due)}`,
            status: `Expected by ${due}`,
          };
    }
    // Never a bare mark: the cell says it is blocked and, where items hold it up, how many.
    case "blocked": {
      const held = step.blocking_items ?? 0;
      const holding = held ? `${held} ${held === 1 ? "item holds" : "items hold"} it up` : "";
      return {
        tone: "blocked",
        text: "blocked",
        sub: holding || undefined,
        status: holding ? `Blocked: ${holding}` : "Blocked",
      };
    }
    case "not_applicable":
      return { tone: "na", text: "n/a", status: "Not applicable" };
    // Nothing on record says it has happened, so it has not.
    case "not_tracked":
      return { tone: "none", text: "", status: "Not happened yet" };
    default:
      return { tone: "none", text: "", status: "Not started" };
  }
};

/** A step waiting on the other side: since the latest report it was sent (a new revision
 * restarts the count, a chase does not), else as the step reads, or, where no action asks
 * yet, the days since the step before it. */
const waitingSince = (
  id: string,
  b: AmazonBuilding,
  since: string,
  what: string,
  reply: string,
  party: string,
): Cell => {
  const step = b.steps[id];
  if (step.state === "open" && step.delivers && step.waiting_since) {
    const days = daysBetween(day(step.waiting_since), today());
    return {
      tone: "open",
      text: `${step.delivers} sent ${days} days ago`,
      sub: "waiting for sign-off",
      status: `${step.delivers} sent ${days} days ago, waiting for ${party}'s sign-off`,
      note: `Sent on ${formatDate(day(step.waiting_since))}.`,
    };
  }
  if (step.state === "open" && !step.date && !step.due) {
    return {
      tone: "open",
      text: "not sent",
      status: `Not sent to ${party} yet`,
    };
  }
  if (step.state === "open" && step.date && !step.due && !step.waiting_since) {
    return {
      tone: "open",
      text: "sent",
      status: `Sent on ${formatDate(day(step.date))}, no reply from ${party} on record`,
    };
  }
  if (step.state === "open") return stepCell(id, step);
  const from = b.steps[since];
  if (step.state !== "not_tracked" || from.state !== "done" || !from.date) {
    return stepCell(id, step);
  }
  const days = daysBetween(day(from.date), today());
  return {
    tone: "none",
    text: `${days} days since`,
    status: `Not happened yet, ${days} days since ${what}`,
    note: `No record of ${reply} yet. It has been ${days} days since ${what} on ${formatDate(day(from.date))}.`,
  };
};

/** Legal's record of the guarantee: delivered and when it was signed, and whether Legal
 * still holds a point open on it, never Legal's own words for that point. */
const guaranteeCell = (b: AmazonBuilding): Cell => {
  const step = b.steps.pcg;
  if (step.state === "done") {
    const signed = step.date
      ? `signed on ${formatDate(day(step.date))}`
      : "signed date not on record";
    return step.note
      ? {
          tone: "done",
          text: "✓ delivered",
          sub: "coverage open",
          status: `Delivered, ${signed}`,
          note: "Coverage open: Legal holds an open requirement on it.",
        }
      : { tone: "done", text: "✓ delivered", status: `Delivered, ${signed}` };
  }
  if (step.state === "open") {
    // Only a due date Legal recorded for it; the page never works one out of the SPPA.
    if (!step.due) return { tone: "open", text: "awaited", status: "Awaited" };
    const due = day(step.due);
    const past = due < today();
    return {
      tone: past ? "blocked" : "open",
      text: "awaited",
      sub: `${past ? "was due" : "due"} ${when(step.due)}`,
      status: `Awaited; ${past ? "was due" : "due by"} ${formatDate(due)}, as Legal records it`,
    };
  }
  if (step.state === "not_tracked") {
    return { tone: "none", text: "", status: "Not delivered yet" };
  }
  return stepCell("pcg", step);
};

/** Amazon's design inputs: how many have come in and how many have not, as the backend
 * counts them, and when they are needed by. Never blocked by its own items: Late, in red
 * text and outline, once any it waits for is past its needed-by date. */
const inputsCell = (b: AmazonBuilding, need: NeedBy): Cell => {
  const step = b.steps.inputs;
  if (step.state === "done") {
    return {
      tone: "done",
      text: "✓",
      status: step.date
        ? `All received, last on ${formatDate(day(step.date))}`
        : "All received",
    };
  }
  // The count stays in view whatever the state: blocked (an override) or late shows under
  // it, never in its place.
  if (step.detail && (step.state === "open" || step.state === "blocked")) {
    const late = lateInputs(b, need, today());
    const blocked = step.state === "blocked";
    const by = earliestNeededBy(b, late.length ? late : designInputs(b).missing, need);
    const needed = by ? `; ${late.length ? "late, was needed by" : "needed by"} ${formatDate(by)}` : "";
    return {
      ...countsCell(step.detail, designInputs(b).missing, late.length, blocked),
      status: `${blocked ? "Blocked. " : ""}Waiting on Amazon: ${step.detail}${needed}`,
    };
  }
  if (step.detail && step.state === "not_tracked") {
    return {
      tone: "none",
      text: step.detail,
      status: `${step.detail[0].toUpperCase()}${step.detail.slice(1)}`,
    };
  }
  if (step.state === "not_tracked") {
    return { tone: "none", text: "", status: "No items listed yet" };
  }
  return stepCell("inputs", step);
};

/** Amazon's own roof condition report, which the inspection card reports beside ours. */
const roofReportLine = (b: AmazonBuilding): string => {
  const report = b.needs?.find((n) => n.key === "roof_condition_report");
  if (report?.status === "received") {
    const on = report.date
      ? ` on ${formatDate(day(report.date))}`
      : ", date not on record";
    const email = report.source ? `, email \u201c${report.source}\u201d` : "";
    return `Amazon's roof condition report: received${on}${email}`;
  }
  if (report?.status === "not_needed") {
    return "Amazon's roof condition report: not needed here";
  }
  return "Amazon's roof condition report: not received";
};

/** Roof inspection & remediation on Step 2, from the roof history
 * (amazon_overview._roof_step): done once the roof is ready for PV; under way on the
 * remediation's stage or the report relied on; a stop shows only that it stopped. */
/** How many of a step's items from Amazon are in and how many are not, as the cell's
 * text, "5 received, 15 not received", and under it how many of those not in hold work up
 * and how many are late, "(2 blocking, 4 late)" (Noaa, 19 Oct 2026). Amazon design inputs
 * and the roof read alike. A part that is nought is left out; a step blocked with no item
 * of its own holding it says "blocked" there. The step late or blocked reads red, as the
 * legend's "Blocked or late" says; items that hold up a later step do not turn it red. */
const countsCell = (
  counts: string,
  items: AmazonNeed[],
  late: number,
  blocked: boolean,
): Pick<Cell, "text" | "sub" | "tone" | "subKept"> => {
  const holding = items.filter(blocksItsStep).length;
  const parts = [
    holding ? `${holding} blocking` : blocked ? "blocked" : "",
    late ? `${late} late` : "",
  ].filter(Boolean);
  return {
    text: counts,
    sub: parts.length ? `(${parts.join(", ")})` : undefined,
    subKept: true,
    tone: late || blocked ? "blocked" : "open",
  };
};

/** The roof's items from Amazon (``stepItems``) as ``countsCell`` reads them; null where
 * it lists none, so the cell keeps its mark. */
const roofCounts = (
  b: AmazonBuilding,
  need: NeedBy,
  blocked: boolean,
): Pick<Cell, "text" | "sub" | "tone" | "subKept"> | null => {
  const items = stepItems(b, "roof");
  if (!items.length) return null;
  const still = items.filter(owed);
  const received = items.filter((n) => n.status === "received").length;
  const partial = items.filter((n) => n.status === "partial").length;
  const late = still.filter((n) => isLateItem(b, n, need, today())).length;
  return countsCell(
    [
      `${received} received`,
      partial ? `${partial} partially received` : "",
      `${still.length - partial} not received`,
    ]
      .filter(Boolean)
      .join(", "),
    still,
    late,
    blocked,
  );
};

const roofCell = (b: AmazonBuilding, need: NeedBy): Cell => {
  const step = b.steps.roof;
  const lines = [roofReportLine(b)];
  // The counts take the mark's place; a blocked step says so under them, with how many
  // items hold it up where it knows.
  const shown = (cell: Cell): Cell => {
    const counts = roofCounts(b, need, cell.tone === "blocked");
    return counts ? { ...cell, ...counts } : cell;
  };
  const on = step.date
    ? ` on ${formatDate(day(step.date))}`
    : ", date not on record";
  if (step.state === "done") {
    return {
      tone: "done",
      text: "✓",
      status: `Ready for PV${on}`,
      lines,
    };
  }
  if (step.state === "blocked" && step.outcome === "dont_proceed") {
    return {
      tone: "blocked",
      text: "don't proceed",
      status: "Don't proceed: see Step 3",
      lines,
    };
  }
  if (step.state === "open") {
    return shown({
      tone: "open",
      text: "●",
      status: step.detail
        ? `${step.detail}${step.date ? ` (${formatDate(day(step.date))})` : ""}`
        : "In progress",
      lines,
    });
  }
  return shown({ ...stepCell("roof", step), lines });
};

/** What design approval waits on before it can start, in the words the page uses: the
 * design inputs and the detailed site visit the updated designs are built on. */
const DR3_WAITS_ON: [string, string][] = [
  ["inputs", "design inputs"],
  ["access2", "detailed site visit"],
  ["design", "updated designs"],
];

/** "a", "a and b", "a, b, and c". */
const listed = (items: string[]): string =>
  items.length < 3
    ? items.join(" and ")
    : `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;

/** Design approval reads Pending whatever its record says (Noaa, 19 Oct 2026); the hover
 * card names what it still waits on. */
const dr3Cell = (b: AmazonBuilding): Cell => {
  const pending = DR3_WAITS_ON.filter(([id]) => !isSettled(b, id)).map(([, w]) => w);
  return {
    tone: "none",
    text: "Pending",
    status: pending.length ? `Pending ${listed(pending)}` : "Pending",
  };
};

/** The platform site's status: under way from a started status, finished once testing
 * begins. Neither carries a date. */
const installCell = (b: AmazonBuilding): Cell => {
  const step = b.steps.install;
  if (step.state === "done") {
    return {
      tone: "done",
      text: "✓",
      status: "Installation finished (testing begun)",
    };
  }
  if (step.state === "open") {
    return {
      tone: "done",
      text: "✓",
      status: "Started: installation under way, start date not on record",
    };
  }
  if (step.state === "blocked" || step.state === "not_applicable") {
    return stepCell("install", step);
  }
  const due = b.contract_dates?.install;
  return {
    tone: "none",
    text: "",
    status: `Not started; ${due ? `contract start date ${formatDate(day(due))}` : "contract start date not on record"}`,
  };
};

/** A freehold building has no landlord: Amazon Real Estate approves the works instead. */
export const freeholdCell = (b: AmazonBuilding): Cell => {
  const step = b.steps.llsignoff;
  const shownAs = {
    title: "Amazon Real Estate approval",
    owner: "amazon" as const,
  };
  return step.state === "done"
    ? {
        tone: "done",
        text: "✓ Amazon RE",
        status: "Amazon Real Estate approved",
        shownAs,
      }
    : {
        shownAs,
        tone: "none",
        text: "Amazon RE approval",
        status: "Freehold: Amazon Real Estate approves in place of a landlord",
        note: "No landlord pack: Amazon owns the building, so Amazon Real Estate approves the works.",
      };
};

/** How the structural and sign-off steps' detail opens while firms disagree and nobody
 * has chosen.
 * Must agree with amazon_overview.ENGINEERS_DISAGREE (tests/unit/test_amazon_overview.py). */
const ENGINEERS_DISAGREE = "Engineers disagree";

/** The structural assessment: whether its report is delivered. The column gives no size
 * or limit; what the report concluded is its finding, on the card. */
const structuralCell = (b: AmazonBuilding): Cell => {
  const step = b.steps.structural;
  if (step.state === "open" && step.detail.startsWith(ENGINEERS_DISAGREE)) {
    return {
      tone: "open",
      text: "engineers disagree",
      status: step.detail,
    };
  }
  const cell = stepCell("structural", step);
  if (cell.tone !== "done") return cell;
  const viable = step.roof_viable;
  return {
    ...cell,
    text: viable ? `✓ (${viableShort(viable)})` : cell.text,
    gap: viable ? viableGap(viable) : undefined,
    status: cell.status.replace(/^Done/, "Report delivered"),
    lines: viable ? [viableLine(viable)] : undefined,
  };
};

type RoofViable = NonNullable<BuildingStep["roof_viable"]>;

/** "60%" or "95-96%", as the structural card writes it. */
export const viableShare = (v: RoofViable): string =>
  v.min_pct === v.max_pct ? `${v.min_pct}%` : `${v.min_pct}-${v.max_pct}%`;

/** The structural cell's dot, read on the top of the report's range (Noaa, 20 Oct 2026):
 * red at 50% of the roof viable or less, amber under 80%, none from 80%. */
const viableGap = (v: RoofViable): GapTone | undefined =>
  v.max_pct <= 50 ? "red" : v.max_pct < 80 ? "amber" : undefined;

/** The cell's share: an estimate, said so, unless the report finds none or all of the
 * roof viable (Noaa, 19 Oct 2026). */
const viableShort = (v: RoofViable): string =>
  (v.max_pct === 0 || v.min_pct === 100 ? viableShare(v) : `Estimated ${viableShare(v)}`) +
  (v.after_works ? " after works" : "");

const kwpRange = (min: number, max: number): string =>
  min === max
    ? `${min.toLocaleString("en-GB")} kWp`
    : `${min.toLocaleString("en-GB")}-${max.toLocaleString("en-GB")} kWp`;

/** The structural cell's hover line (Noaa, 19 Oct 2026): the share and the kWp it is of
 * the max-out design; none viable is plainly 0 kWp. */
const viableLine = (v: RoofViable): string => {
  const share = `Estimate percentage of evaluated roof viable structurally: ${viableShare(v)}${v.after_works ? " after works" : ""}`;
  if (v.max_pct === 0) return `${share}, representing 0 kWp`;
  if (!v.kwp) return `${share}; no max-out design size is recorded to give it in kWp`;
  return `${share}, representing approximately ${kwpRange(v.kwp.min, v.kwp.max)} (subject to PV redesign)`;
};

/** The kWp the structural reports find viable across ``buildings``, a range where any
 * report gives one, against the kWp the PPAs commit; with the buildings each sum leaves
 * out (no viable share with a size to give it in kWp, no committed kWp). A report finding
 * none viable counts as 0 kWp. */
export const viableCapacity = (
  buildings: AmazonBuilding[],
): {
  min: number;
  max: number;
  committed: number;
  unestimated: number;
  uncommitted: number;
  afterWorks: number;
} => {
  const applies = buildings.filter((b) => b.steps.structural?.state !== "not_applicable");
  const kwp = applies.map((b): [number, number] | null => {
    const v = b.steps.structural?.state === "done" ? b.steps.structural.roof_viable : null;
    if (!v) return null;
    if (v.max_pct === 0) return [0, 0];
    return v.kwp ? [v.kwp.min, v.kwp.max] : null;
  });
  const known = kwp.filter((k): k is [number, number] => k !== null);
  return {
    min: known.reduce((sum, [lo]) => sum + lo, 0),
    max: known.reduce((sum, [, hi]) => sum + hi, 0),
    committed: applies.reduce((sum, b) => sum + (b.committed_kwp ?? 0), 0),
    unestimated: kwp.length - known.length,
    uncommitted: applies.filter((b) => b.committed_kwp == null).length,
    afterWorks: applies.filter(
      (b) =>
        b.steps.structural?.state === "done" &&
        b.steps.structural.roof_viable?.after_works &&
        b.steps.structural.roof_viable.max_pct > 0,
    ).length,
  };
};

/** Amazon's sign-off of our structural assessment (amazon_overview._signoff_step): the
 * requirements still to come, as its card lists them; then, with none left, the wait for
 * Amazon's acceptance; then a newer version waiting. */
const signoffCell = (b: AmazonBuilding): Cell => {
  const step = b.steps.signoff;
  // Not started: nothing is relied on until someone decides between the firms.
  if (step.state === "not_started" && step.detail.startsWith(ENGINEERS_DISAGREE)) {
    return { tone: "none", text: "engineers disagree", status: step.detail };
  }
  const sent = step.date ? `Sent on ${formatDate(day(step.date))}.` : undefined;
  const waited = step.waiting_since
    ? `${daysBetween(day(step.waiting_since), today())} days waiting`
    : undefined;
  // Everything the card lists is a requirement, not received, partially received or
  // received; the cell counts those still to come, as the card does, and never says
  // "point" (Noaa, 25 Oct 2026).
  const toCome = stepItems(b, "signoff").filter(owed);
  const toComeText = (): string => {
    const partial = toCome.filter((n) => n.status === "partial").length;
    const missing = toCome.length - partial;
    return [
      missing ? `${missing} not received` : "",
      partial ? `${partial} partially received` : "",
    ]
      .filter(Boolean)
      .join(", ");
  };
  if (step.state === "open" && step.points_open) {
    // Open feedback the card does not list (only InRange's, or owed by no one on record).
    const points = step.open_points ?? [];
    const ours = points.length > 0 && points.every((p) => p.owed_party === "inrange");
    return {
      tone: "open",
      text: toCome.length
        ? toComeText()
        : `${step.points_open} ${ours ? "on InRange" : "not received"}`,
      status: step.detail,
      note: sent,
    };
  }
  if (step.state === "open" && step.points_open === 0 && waited) {
    return {
      tone: "open",
      text: toCome.length ? toComeText() : "awaiting acceptance",
      sub: waited,
      status: step.detail,
      note: sent,
    };
  }
  if (step.state === "open" && step.delivers && !step.waiting_since) {
    return {
      tone: "open",
      text: `${step.delivers} issued`,
      status: step.detail,
    };
  }
  return waitingSince(
    "signoff",
    b,
    "structural",
    "our assessment",
    "Amazon's sign-off",
    "Amazon",
  );
};

/** Each step's needed-by date (schedule.neededBy), where the page has worked them out. */
export type NeedBy = Record<string, Date> | null;

const CELLS: Record<string, (b: AmazonBuilding, need: NeedBy) => Cell> = {
  pcg: guaranteeCell,
  structural: structuralCell,
  inputs: inputsCell,
  roof: roofCell,
  dr3: dr3Cell,
  // The column asks whether installation has started: under way is a yes.
  install: installCell,
  signoff: signoffCell,
  llsent: (b) => {
    const step = b.steps.llsent;
    const pack = b.steps.llpack;
    if (step.state !== "not_tracked" || pack.state !== "done" || !pack.date) {
      return stepCell("llsent", step);
    }
    const days = daysBetween(day(pack.date), today());
    return {
      tone: "none",
      text: `${days} days since`,
      status: `Not sent on yet, ${days} days since we sent the pack to Amazon`,
      note: `Legal has not recorded an email sending it on to the landlord's side. It has been ${days} days since we sent the pack to Amazon on ${formatDate(day(pack.date))}.`,
    };
  },
  // The landlord's wait starts when the pack reached it, where that is on record.
  llfeedback: (b) =>
    b.steps.llsent.state === "done" && b.steps.llsent.date
      ? waitingSince(
          "llfeedback",
          b,
          "llsent",
          "the pack reached the landlord",
          "the landlord's comments on the pack",
          "the landlord",
        )
      : waitingSince(
          "llfeedback",
          b,
          "llpack",
          "we sent the pack to Amazon",
          "the landlord's comments on the pack",
          "the landlord",
        ),
  gridoffer: (b) => {
    const step = b.steps.gridoffer;
    const terms = offerTerms(b);
    const estimate = estimateTerms(b);
    if (terms === null && estimate !== null) return estimateCell(step, estimate);
    if (terms === null) return stepCell("gridoffer", step);
    const kwAc = offeredKwAc(b);
    const cell = stepCell("gridoffer", { ...step, state: "done", kw_ac: kwAc });
    return { ...cell, gap: ppaShortfall(kwAc, b.committed_kw_ac), status: terms };
  },
};

/** The DNO's budget estimate in place of an offer, as the hover card and Step 3 state it,
 * or null where none stands (amazon_overview._grid_steps). No offer is in hand and none is
 * on its way: one needs a new application, so nothing waits on the DNO. */
export const estimateTerms = (b: AmazonBuilding): string | null => {
  const step = b.steps.gridoffer;
  // A state written over the step on the page wins.
  if (!step.estimate_for || (step.state !== "open" && step.state !== "blocked")) return null;
  const on = step.estimate_on ? ` on ${formatDate(day(step.estimate_on))}` : ", date not recorded";
  return `Budget estimate received${on}, not a formal offer`;
};

const estimateCell = (step: BuildingStep, terms: string): Cell => ({
  tone: step.state === "blocked" ? "blocked" : "open",
  text: "budget estimate",
  sub: step.estimate_on ? when(step.estimate_on) : undefined,
  status: terms,
  lines: [
    `It cannot be accepted. The application of ${formatDate(day(step.estimate_for ?? ""))} ended in this estimate. A formal offer needs a new application.`,
    "Next: InRange decides on a new application.",
  ],
});

/** The DNO's offer as Step 2's hover card states it, or null before one is received. An
 * accepted offer was received, whatever the offer step says. It allows the installed
 * capacity the offer gives (its export beside it), how far that falls short of the PPA's
 * (Step 3's size tile's rule), and the works it needs. Step 3's card for the step reads
 * the same. */
export const offerTerms = (b: AmazonBuilding): string | null => {
  const step = b.steps.gridoffer;
  if (b.steps.gridacc?.state !== "done" && step.state !== "done") return null;
  const offer = theOffer(b);
  const kwAc = offeredKwAc(b);
  const below =
    ppaShortfall(kwAc, b.committed_kw_ac) && kwAc != null && b.committed_kw_ac != null
      ? ` (${Math.round((1 - kwAc / b.committed_kw_ac) * 100)}% below the PPA's ${sizeText(b.committed_kw_ac, "kWac")})`
      : "";
  const works = offerPoint(offer, "works");
  const offered = `Offer received${step.date ? ` on ${formatDate(day(step.date))}` : ""}`;
  const installed = offerFigure(offer, INSTALLED);
  const exported = offerFigure(offer, EXPORT);
  const allows =
    step.kw_ac != null || installed == null
      ? kwAc != null
        ? `allows ${sizeText(kwAc, "kWac")}${exported != null && step.kw_ac == null ? ` export` : ""}`
        : "size not in the offer as Grid wrote it"
      : `allows ${kwText(installed)} installed${exported != null ? `, ${kwText(exported)} export` : ""}`;
  return (
    `${offered}, ${allows}${below}; ` +
    (!works
      ? "works required not recorded by Grid"
      : NO_WORKS.test(works)
        ? "no works required"
        : `works required: ${lowerFirst(works)}`)
  );
};

/** Grid's works point where the offer needs none ("none", "No works required"). */
const NO_WORKS = /^(?:none|no\b)/i;

/** The stages a route is at once the DNO has made its offer. */
const OFFERED = new Set(
  OPEN_ROUTE_STAGES.slice(OPEN_ROUTE_STAGES.indexOf("OFFER RECEIVED")),
);

/** The one offer the page reports: the accepted one, else the furthest on. */
const theOffer = (b: AmazonBuilding): OptionRoute | undefined =>
  parseOptionRoutes(b.grid_options ?? "")
    .filter((r) => !r.ruledOut && OFFERED.has(r.stage))
    .sort(routeOrder)[0];

/** A point of the offer as Grid wrote it ("Grid works:", as Grid's prompt asks, or
 * "Works:", "Works required:", as older rows carry). */
const offerPoint = (
  offer: OptionRoute | undefined,
  label: string,
): string =>
  offer?.points
    .find((p) => p.label.toLowerCase().replace(/^grid\s+/, "").startsWith(label))
    ?.text.trim() ?? "";

/** The figures the offer's capacity gives ("900 kW installed", "1,400 kVA export"). */
const INSTALLED = /(\d[\d,]*(?:\.\d+)?)\s*([kM])(?:W|VA)\w*\s+installed/i;
const EXPORT = /(\d[\d,]*(?:\.\d+)?)\s*([kM])(?:W|VA)\w*\s+(?:full\s+)?export/i;

/** What the DNO's offer allows: the figure someone wrote over it on the page, else the
 * installed capacity the offer gives, else its export where it states no installed figure.
 * Never achievable kWac, which moves with the building's other limits. */
export const offeredKwAc = (b: AmazonBuilding): number | null => {
  const offer = theOffer(b);
  return (
    b.steps.gridoffer.kw_ac ??
    offerFigure(offer, INSTALLED) ??
    offerFigure(offer, EXPORT)
  );
};

/** The portfolio's grid capacity: the kWac the offers in hand allow, against the kWac
 * the PPAs commit across every building the grid offer applies to, with the counts the
 * sums leave out (an offer in hand whose size is not recorded, a building with no
 * committed kWac). */
export const grantedCapacity = (
  buildings: AmazonBuilding[],
): { granted: number; committed: number; unsized: number; uncommitted: number } => {
  const applies = buildings.filter((b) => b.steps.gridoffer?.state !== "not_applicable");
  const inHand = applies.filter((b) => offerTerms(b) !== null);
  const sizes = inHand.map(offeredKwAc);
  return {
    granted: sizes.reduce<number>((sum, kw) => sum + (kw ?? 0), 0),
    committed: applies.reduce((sum, b) => sum + (b.committed_kw_ac ?? 0), 0),
    unsized: sizes.filter((kw) => kw == null).length,
    uncommitted: applies.filter((b) => b.committed_kw_ac == null).length,
  };
};

const offerFigure = (
  offer: OptionRoute | undefined,
  pattern: RegExp,
): number | null => {
  const m = pattern.exec(offerPoint(offer, "capacity"));
  if (!m) return null;
  const value = Number(m[1].replace(/,/g, ""));
  return m[2].toUpperCase() === "M" ? value * 1000 : value;
};

const kwText = (kw: number): string =>
  `${kw.toLocaleString("en-GB", { maximumFractionDigits: 1 })} kW`;

const lowerFirst = (text: string): string =>
  /^[A-Z][a-z]/.test(text) ? `${text[0].toLowerCase()}${text.slice(1)}` : text;

/** A step's cell. One the items it needs hold up says which, and whose they are, in place
 * of a bare "Blocked". */
export const cellFor = (id: string, b: AmazonBuilding, need: NeedBy = null): Cell => {
  const blocked = b.steps[id]?.state === "blocked";
  // How many hold it up, as its "Needed from Amazon" list counts them: open points too.
  const counted = blocked
    ? {
        ...b,
        steps: { ...b.steps, [id]: { ...b.steps[id], blocking_items: heldItems(b, id).length } },
      }
    : b;
  const cell = CELLS[id] ? CELLS[id](counted, need) : stepCell(id, counted.steps[id]);
  const held = blocked ? waitingText(b, id) : "";
  return held && cell.status === "Blocked" ? { ...cell, status: held } : cell;
};

/** The sends the page names in full: who sent what to whom, when. */
export const SENT_TO: Record<string, string> = {
  llpack: "Sent to Amazon",
  llsent: "Sent to landlord",
  llfeedback: "Landlord feedback",
};

/** The step's send is named in full on its card: to whom, when, by whom and the email,
 * each missing one said so. */
export const namesSend = (id: string, step: BuildingStep): boolean =>
  Boolean(SENT_TO[id]) && step.state === "done" && Boolean(step.date);

/** The steps whose card says the same things for every building: its status line and the
 * send behind it, never an agent's own words, which differ building to building. Only a
 * blocker's reason is shown, as what holds it up, and an assessment's finding: what the
 * report concluded, which Building readiness records for this card. */
const FIXED = new Set([
  "gridoffer",
  "structural",
  "signoff",
  "llpack",
  "llsent",
  "llfeedback",
  "pcg",
  "inputs",
  "roof",
  "dr3",
  "install",
]);

/** The line under the hover card's status: the record's own detail where the column takes
 * it, nothing where the status and the send say it all, else what the column means. */
export const lineOf = (
  b: AmazonBuilding,
  id: string,
): { text: string; means: boolean } => {
  const step = b.steps[id];
  const means = { text: firstSentence(MEANS[id] ?? ""), means: true };
  if (FIXED.has(id)) {
    if (id === "dr3") return means;
    if (id === "structural" && step.finding)
      return { text: step.finding, means: false };
    // A roof that passed says what the inspection found; anything else it found is Step 3's.
    if (id === "roof" && step.state === "done" && step.finding) {
      return { text: step.finding, means: false };
    }
    if (id === "roof" && step.state === "blocked")
      return { text: "", means: false };
    if (step.state === "blocked" && step.detail) {
      return { text: firstSentence(step.detail), means: false };
    }
    return step.state === "done" || step.state === "open"
      ? { text: "", means: false }
      : means;
  }
  return step.detail && !step.delivers && !namesSend(id, step)
    ? { text: firstSentence(step.detail), means: false }
    : means;
};

export const recordLine = (b: AmazonBuilding, id: string): string =>
  lineOf(b, id).text;

/** The cell as the page shows it: its words written over where someone has. */
export const shownCell = (
  b: AmazonBuilding,
  id: string,
  o: Overrides,
  need: NeedBy = null,
): Cell => {
  const cell =
    b.freehold && id === "llsignoff" ? freeholdCell(b) : cellFor(id, b, need);
  const words = wordingOf(o, stepKey(id, "cell"));
  if (words !== undefined) return { ...cell, text: words, sub: undefined, subKept: undefined };
  // The line under the cell's bold value goes where it says nothing the value does not.
  return cell.sub && !cell.subKept && repeatsBold(cell.sub, cell.text)
    ? { ...cell, sub: undefined }
    : cell;
};

/** The hover card's line from the record, as the page shows it. */
export const shownLine = (
  b: AmazonBuilding,
  id: string,
  o: Overrides,
): string => wordingOf(o, stepKey(id, "line")) ?? recordLine(b, id);
